/*
 * The admin side's small attachment rules (S47): the library page's address,
 * the href an attachment link gets in the editor preview, and the browser's
 * check of a file before it is uploaded.
 */

import {
  ATTACHMENT_SNIFF_BYTES,
  inspectAttachment,
  normaliseAttachmentFileName,
} from '@accotool-kb/shared';

/** The library page. */
export const ADMIN_ATTACHMENTS_PATH = '/admin/attachments';

/** The row of one file on the library page, as an element id and a fragment. */
export function attachmentRowId(attachmentId: string): string {
  return `attachment-${attachmentId.toLowerCase()}`;
}

/**
 * Where an attachment link points in the editor preview: the file's row in the
 * library. A plain click is taken over and downloads (see MarkdownEditor); a
 * click into a new tab opens the library at the file, which is the useful place
 * for an administrator to land - the admin download itself needs a token no
 * href can carry.
 */
export function adminAttachmentHref(attachmentId: string): string {
  return `${ADMIN_ATTACHMENTS_PATH}#${attachmentRowId(attachmentId)}`;
}

/** The id an adminAttachmentHref names, or null for any other href. */
export function parseAdminAttachmentHref(href: string): string | null {
  const match = /^\/admin\/attachments#attachment-([0-9a-f-]{36})$/i.exec(href);
  return match ? match[1]!.toLowerCase() : null;
}

export type AttachmentFileCheck = { ok: true; fileName: string } | { ok: false; fileName: string; message: string };

/**
 * The shared gate, run in the browser before any bytes move: the name
 * normalised as the server will normalise it, then the name rules, the size and
 * whether the first bytes are the kind of file the name says. Advisory - the
 * server runs the same checks on the whole file - but it means a renamed .exe
 * or a 30 MB video is refused at once rather than after the upload.
 */
export async function checkAttachmentFile(file: File): Promise<AttachmentFileCheck> {
  const fileName = normaliseAttachmentFileName(file.name);
  const head = new Uint8Array(await file.slice(0, ATTACHMENT_SNIFF_BYTES).arrayBuffer());
  const inspection = inspectAttachment(fileName, head, file.size);
  return inspection.ok ? { ok: true, fileName } : { ok: false, fileName, message: inspection.message };
}
